import { sportLabel } from "@/lib/conditions/sport-meta";
import type { SkiV3BySport } from "@/lib/conditions/ski-v3-carriers";
import { scoreBandTextClass } from "@/lib/conditions/score-colour";
import { SKI_SPORTS, skiBandRows, type SkiBandSportCell } from "./ski-v3-view";
import type { SkiSport } from "@/lib/conditions/ski/types";

const COVER_CHIP: Record<string, string> = {
  "Full cover": "bg-emerald-100 text-emerald-800",
  "Thin in places": "bg-amber-100 text-amber-800",
  Marginal: "bg-amber-100 text-amber-800",
  "Patchy (grass showing)": "bg-orange-100 text-orange-800",
  "Ribbon only": "bg-red-100 text-red-800",
  "Bare or closed": "bg-slate-100 text-slate-700",
};

function Cell({ sport, cell, active }: { sport: SkiSport; cell: SkiBandSportCell | null; active: boolean }) {
  return (
    <div className={`rounded-[10px] border px-2.5 py-2 ${active ? "border-gold/40 bg-gold-pale" : "border-border-light bg-warm-bg"}`}>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.5px] text-text-muted">{sportLabel(sport)}</dt>
      <dd className="mt-0.5">
        {cell === null ? (
          <span className="text-[13px] text-text-muted">Not scored</span>
        ) : cell.score === null ? (
          <span className="text-[13px] font-bold leading-tight text-navy">{cell.label}</span>
        ) : (
          <>
            <span className={`text-[17px] font-bold tabular-nums leading-tight ${scoreBandTextClass(cell.score / 10)}`}>{cell.score}</span>
            <span className="ml-1 text-[11.5px] text-text-muted">{cell.label}</span>
          </>
        )}
      </dd>
    </div>
  );
}

/**
 * Engine bands (base, mid, summit, or up to 5): piste, off-piste and touring band scores
 * (Closed where the status withholds them), the band's cover word, runs open and the piste
 * surface blocks as a sentence. Renders nothing without stored v3 bands (revision 2 rows).
 */
export function SkiBandsPanel({ bySport, activeSport }: { bySport: SkiV3BySport | null | undefined; activeSport?: string }) {
  const rows = skiBandRows(bySport);
  if (rows.length === 0) return null;
  const sports = SKI_SPORTS.filter((sport) => rows.some((row) => row.sports[sport] !== null));
  const cols = sports.length === 3 ? "grid-cols-3" : sports.length === 2 ? "grid-cols-2" : "grid-cols-1";
  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7" data-ski-bands="">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">Scores by elevation band</h3>
        <span className="text-[11px] text-text-muted">Out of 100, for an experienced skier</span>
      </div>
      <ol className="divide-y divide-border-light">
        {rows.map((row) => (
          <li key={row.key} className="py-3.5 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="text-[13px] font-semibold text-navy">
                {row.label}{" "}
                <span className="font-normal text-text-muted tabular-nums">{row.elevationM.toLocaleString("en-GB")} m</span>
              </h4>
              {row.cover && (
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${COVER_CHIP[row.cover] ?? "bg-slate-100 text-slate-700"}`}>
                  {row.cover}
                </span>
              )}
            </div>
            <dl className={`mt-2 grid ${cols} gap-2`}>
              {sports.map((sport) => (
                <Cell key={sport} sport={sport} cell={row.sports[sport]} active={sport === activeSport} />
              ))}
            </dl>
            {(row.openPct !== null || row.surface) && (
              <div className="mt-2 space-y-0.5 text-[12px] leading-[1.5] text-text-body">
                {row.openPct !== null && <p>Runs open: {row.openPct} %</p>}
                {row.surface && <p>Piste surface: {row.surface}</p>}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
